import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import {
  hasPermissionToViewData,
  isUserAuthorized,
  showCreateListingLinkForUser,
} from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { fetchFilterOptionCounts, loadData, setPerPageOverride } from './SearchPage.duck';

import {
  H3,
  H5,
  NamedLink,
  NamedRedirect,
  Page,
  IconArrowHead,
  IconSpinner,
} from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
  getDatesAndSeatsMaybe,
  getSearchPageResourceLocatorStringParams,
  isPromotionsPath,
  getActiveCategoryName,
  getActiveBrandName,
  getActiveConnectivityName,
  getActiveCompatibilityName,
  getFilterOptionCounts,
} from './SearchPage.shared';
import { normalizeIncomingSearchParams } from '../../util/searchParamMappers';
import { getOptimalPerPage } from '../../util/getColumnsPerRow';
import {
  findSlugEntry,
  getCategoryEntryById,
  getRelatedLinks,
  getSeoByCategoryIds,
  getSlugSearchParams,
  getSubcategoryEntryByIds,
  ensureSlugEntries,
  getSlugEntriesCount,
  areSlugEntriesLoaded,
} from '../../util/categorySlugMap';

import FilterComponent from './FilterComponent';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';
import SearchBreadcrumb from './SearchBreadcrumb';

import css from './SearchPage.module.css';

const MODAL_BREAKPOINT = 768; // Search is in modal on mobile layout

// SortBy component has its content in dropdown-popup.
// With this offset we move the dropdown a few pixels on desktop layout.
const FILTER_DROPDOWN_OFFSET = -14;
const isDealsParamActive = value =>
  value === 'true' || value === true || value === '1' || value === 1;

export class SearchPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isMobileModalOpen: false,
      currentQueryParams: validUrlQueryParamsFromProps(props),
      isAsideCollapsed: false,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1920, // Default to large screen for SSR
      slugDataReady: !props?.params?.slug || (areSlugEntriesLoaded() && getSlugEntriesCount() > 0),
    };

    this.onOpenMobileModal = this.onOpenMobileModal.bind(this);
    this.onCloseMobileModal = this.onCloseMobileModal.bind(this);
    this.toggleAside = this.toggleAside.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.setCtaBannerRef = this.setCtaBannerRef.bind(this);
    this.updateCtaVisibility = this.updateCtaVisibility.bind(this);

    // Filter functions
    this.resetAll = this.resetAll.bind(this);
    this.getHandleChangedValueFn = this.getHandleChangedValueFn.bind(this);

    // SortBy
    this.handleSortBy = this.handleSortBy.bind(this);

    // Track last canonicalized search to prevent infinite loops
    this.lastCanonicalizedSearch = null;
    this.canonicalizeTimeout = null;

    // Cache for dynamic brand options to avoid recreating on every render
    this.cachedBrandOptions = null;
    this.cachedListingsForBrands = null;
    this.cachedListingFieldsConfig = null;
    this.cachedListingFieldsConfigInput = null;

    this.lastFilterCountsKey = null;
    this.ctaBannerRef = null;
  }

  setCtaBannerRef(ref) {
    this.ctaBannerRef = ref;
  }

  updateCtaVisibility() {
    if (!this.ctaBannerRef) return;
    const banner = this.ctaBannerRef;
    const buttons = Array.from(banner.querySelectorAll(`.${css.categoryCtaButton}`));
    if (buttons.length === 0) return;

    buttons.forEach(btn => btn.classList.remove(css.categoryCtaButtonHidden));
    if (banner.scrollWidth > banner.clientWidth) {
      const lastButton = buttons[buttons.length - 1];
      if (lastButton) {
        lastButton.classList.add(css.categoryCtaButtonHidden);
      }
    }
  }

  componentDidMount() {
    // Defer canonicalization to avoid fighting with initial client-side replace
    setTimeout(() => this.ensureCanonicalQuery(), 0);

    this.processFilterCountFetch();
    const { params: currentPathParams = {}, config } = this.props;
    if (typeof window !== 'undefined') {
      if (currentPathParams?.slug && (!areSlugEntriesLoaded() || getSlugEntriesCount() === 0)) {
        this.setState({ slugDataReady: false });
      }
      ensureSlugEntries(config?.marketplaceRootURL)
        .then(() =>
          this.setState({ slugDataReady: getSlugEntriesCount() > 0 || !currentPathParams?.slug })
        )
        .catch(() =>
          this.setState({ slugDataReady: getSlugEntriesCount() > 0 || !currentPathParams?.slug })
        );
    }

    // Track window width for dynamic perPage calculation
    if (typeof window !== 'undefined') {
      this.handleResize();
      window.addEventListener('resize', this.handleResize);
      // Update perPage on initial mount
      setTimeout(() => this.updatePerPageIfNeeded(), 100);
    }
    setTimeout(() => this.updateCtaVisibility(), 0);
  }

  componentWillUnmount() {
    // Cleanup timeout to prevent memory leaks
    if (this.canonicalizeTimeout) {
      clearTimeout(this.canonicalizeTimeout);
    }

    // Remove resize listener
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const currentSearch = this.props.location.search;
    const prevSearch = prevProps.location.search;
    const { windowWidth } = this.state;
    const prevWindowWidth = prevState.windowWidth;

    // If window width changed significantly, update perPage in URL if needed
    if (Math.abs(windowWidth - prevWindowWidth) > 50 && typeof window !== 'undefined') {
      this.updatePerPageIfNeeded();
    }

    const currentSlug = this.props.params?.slug;
    const prevSlug = prevProps.params?.slug;
    if (currentSlug && currentSlug !== prevSlug && typeof window !== 'undefined') {
      const { config } = this.props;
      if (!areSlugEntriesLoaded() || getSlugEntriesCount() === 0) {
        this.setState({ slugDataReady: false });
      }
      ensureSlugEntries(config?.marketplaceRootURL)
        .then(() => this.setState({ slugDataReady: getSlugEntriesCount() > 0 }))
        .catch(() => this.setState({ slugDataReady: getSlugEntriesCount() > 0 }));
    }

    if (
      this.props.params?.slug &&
      !prevState.slugDataReady &&
      this.state.slugDataReady &&
      typeof this.props.onLoadData === 'function'
    ) {
      this.props.onLoadData(this.props.params, this.props.location.search, this.props.config);
    }

    // console.log('🔄 SearchPage componentDidUpdate:', {
    //   component: 'SearchPageWithGrid',
    //   prevSearch,
    //   currentSearch,
    //   lastCanonicalizedSearch: this.lastCanonicalizedSearch,
    //   shouldCanonicalize: prevSearch !== currentSearch && this.lastCanonicalizedSearch !== currentSearch,
    //   timestamp: new Date().toISOString()
    // });

    // Only canonicalize if search actually changed and it's not the result of our own canonicalization
    // Add debounce to prevent rapid successive calls
    if (prevSearch !== currentSearch && this.lastCanonicalizedSearch !== currentSearch) {
      // Debounce canonicalization to avoid excessive calls during rapid filter changes
      if (this.canonicalizeTimeout) {
        clearTimeout(this.canonicalizeTimeout);
      }
      this.canonicalizeTimeout = setTimeout(() => {
        this.ensureCanonicalQuery();
      }, 100);
    }
    // Reset filter counts cache when a new search starts
    if (!prevProps.searchInProgress && this.props.searchInProgress) {
      this.lastFilterCountsKey = null;
    }
    this.processFilterCountFetch();
    if (
      prevProps.location.search !== this.props.location.search ||
      prevProps.listings !== this.props.listings ||
      prevState.isAsideCollapsed !== this.state.isAsideCollapsed
    ) {
      setTimeout(() => this.updateCtaVisibility(), 0);
    }
  }

  ensureCanonicalQuery() {
    const {
      history,
      routeConfiguration,
      location,
      config,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(c => c.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const { mapSearch, page, ...raw } = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });
    const normalized = normalizeIncomingSearchParams(raw, filterConfigs);
    const validated = validFilterParams(normalized, filterConfigs, false);
    const canonicalFrom = Object.keys(validated).length > 0 ? validated : normalized;

    // Include page parameter in canonical URL if it exists
    let canonicalParams = page ? { ...canonicalFrom, page } : canonicalFrom;

    let { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );
    const isPromotionsRoute = routeName === 'PromotionsPage';

    const categoryId = canonicalFrom.pub_categoryLevel1 || canonicalFrom.category;
    const subcategoryId = canonicalFrom.pub_categoryLevel2 || canonicalFrom.subcategory;
    if (!currentPathParams.slug && categoryId && !isPromotionsRoute) {
      const subcategoryEntry = subcategoryId
        ? getSubcategoryEntryByIds(categoryId, subcategoryId)
        : null;
      const categoryEntry = getCategoryEntryById(categoryId);
      const targetSlug = subcategoryEntry?.slug || categoryEntry?.slug;
      if (targetSlug) {
        routeName = 'SearchPageWithSlug';
        pathParams = { slug: targetSlug };
        canonicalParams = omit(canonicalParams, [
          'category',
          'subcategory',
          'pub_categoryLevel1',
          'pub_categoryLevel2',
        ]);
      }
    }

    const canonical = createResourceLocatorString(
      routeName,
      routeConfiguration,
      pathParams,
      canonicalParams
    );
    const current = `${location.pathname}${location.search}`;

    // Prevent infinite loops: only replace if search differs AND we haven't just canonicalized this exact search
    const currentSearch = new URL('http://x' + current).search;
    const canonicalSearch = new URL('http://x' + canonical).search;

    // console.log('🔧 SearchPage ensureCanonicalQuery:', {
    //   component: 'SearchPageWithGrid',
    //   current,
    //   canonical,
    //   currentSearch,
    //   canonicalSearch,
    //   lastCanonicalizedSearch: this.lastCanonicalizedSearch,
    //   shouldReplace: canonicalSearch !== currentSearch && this.lastCanonicalizedSearch !== canonicalSearch,
    //   timestamp: new Date().toISOString()
    // });

    if (canonicalSearch !== currentSearch && this.lastCanonicalizedSearch !== canonicalSearch) {
      this.lastCanonicalizedSearch = canonicalSearch;
      history.replace(canonical);
    }
  }

  // Invoked when a modal is opened from a child component,
  // for example when a filter modal is opened in mobile view
  onOpenMobileModal() {
    this.setState({ isMobileModalOpen: true });
  }

  // Invoked when a modal is closed from a child component,
  // for example when a filter modal is opened in mobile view
  onCloseMobileModal() {
    this.setState({ isMobileModalOpen: false });
  }

  toggleAside() {
    this.setState(prevState => ({
      isAsideCollapsed: !prevState.isAsideCollapsed,
    }));
  }

  // Reset all filter query parameters
  resetAll(e) {
    const { history, routeConfiguration, config, location } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    // Reset state
    this.setState({ currentQueryParams: {} });

    // Reset routing params - remove all filter params and also deals
    const queryParams = omit(urlQueryParams, [...filterQueryParamNames, 'deals']);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  getHandleChangedValueFn(useHistoryPush) {
    const {
      history,
      routeConfiguration,
      config,
      location,
      params: currentPathParams = {},
    } = this.props;
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    return updatedURLParams => {
      const rawQueryParams = parse(location.search);
      const hasKeywordChange = Object.prototype.hasOwnProperty.call(updatedURLParams, 'keywords');
      const dealsMaybe =
        isDealsParamActive(rawQueryParams.deals) && !hasKeywordChange
          ? { deals: rawQueryParams.deals }
          : {};
      const updater = prevState => {
        const { address, bounds, keywords } = urlQueryParams;
        const mergedQueryParams = { ...urlQueryParams, ...prevState.currentQueryParams };

        // Address and bounds are handled outside of MainPanel.
        // I.e. TopbarSearchForm && search by moving the map.
        // We should always trust urlQueryParams with those.
        // The same applies to keywords, if the main search type is keyword search.
        const keywordsMaybe = isMainSearchTypeKeywords(config) ? { keywords } : {};
        const datesAndSeatsMaybe = getDatesAndSeatsMaybe(mergedQueryParams, updatedURLParams);

        const newQueryParams = omitLimitedListingFieldParams(
          {
            ...mergedQueryParams,
            ...updatedURLParams,
            ...keywordsMaybe,
            ...datesAndSeatsMaybe,
            address,
            bounds,
          },
          filterConfigs
        );

        return {
          currentQueryParams: newQueryParams,
        };
      };

      const callback = () => {
        if (useHistoryPush) {
          const searchParams = this.state.currentQueryParams;
          const search = cleanSearchFromConflictingParams(searchParams, filterConfigs, sortConfig);

          const hasCategoryFilterChange = [
            'category',
            'subcategory',
            'subsubcategory',
            'pub_categoryLevel1',
            'pub_categoryLevel2',
            'pub_categoryLevel3',
          ].some(key => Object.prototype.hasOwnProperty.call(updatedURLParams, key));
          let { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
            routeConfiguration,
            location
          );
          const isPromotionsRoute = routeName === 'PromotionsPage';
          let nextSearch = { ...search, ...dealsMaybe };
          if (hasCategoryFilterChange && !isPromotionsRoute) {
            const categoryId = search.pub_categoryLevel1 || search.category;
            const subcategoryId = search.pub_categoryLevel2 || search.subcategory;
            const slugEntry = subcategoryId
              ? getSubcategoryEntryByIds(categoryId, subcategoryId)
              : getCategoryEntryById(categoryId);
            if (slugEntry?.slug) {
              routeName = 'SearchPageWithSlug';
              pathParams = { slug: slugEntry.slug };
              nextSearch = omit(search, [
                'category',
                'subcategory',
                'subsubcategory',
                'pub_categoryLevel1',
                'pub_categoryLevel2',
                'pub_categoryLevel3',
              ]);
            } else if (currentPathParams?.slug) {
              routeName = 'SearchPage';
              pathParams = {};
            }
          }

          const newUrl = createResourceLocatorString(
            routeName,
            routeConfiguration,
            pathParams,
            nextSearch
          );
          const currentUrl = `${location.pathname}${location.search}`;

          // Normalize URLs for comparison to avoid unnecessary navigation
          const normalizedNewUrl =
            new URL('http://x' + newUrl).pathname + new URL('http://x' + newUrl).search;
          const normalizedCurrentUrl =
            new URL('http://x' + currentUrl).pathname + new URL('http://x' + currentUrl).search;

          // Only push if the URL is actually different
          if (normalizedNewUrl !== normalizedCurrentUrl) {
            history.push(newUrl);
          }
        }
      };

      this.setState(updater, callback);
    };
  }

  handleSortBy(urlParam, values) {
    const { history, routeConfiguration, location } = this.props;
    const urlQueryParams = validUrlQueryParamsFromProps(this.props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(routeName, routeConfiguration, pathParams, queryParams)
    );
  }

  maybeRequestFilterCounts(validQueryParams, availableFilters) {
    if (!Array.isArray(availableFilters) || availableFilters.length === 0) {
      this.lastFilterCountsKey = null;
      return;
    }

    const paramsForFetch = { ...validQueryParams };
    delete paramsForFetch.page;
    delete paramsForFetch.perPage;

    // Include deals parameter from URL if present (it's not a valid filter param)
    if (this.props.location?.search) {
      const rawQueryParams = parse(this.props.location.search);
      const isPromotionsPage = isPromotionsPath(this.props.location?.pathname);
      if (
        rawQueryParams.deals === 'true' ||
        rawQueryParams.deals === true ||
        rawQueryParams.deals === '1' ||
        rawQueryParams.deals === 1 ||
        isPromotionsPage
      ) {
        paramsForFetch.deals = rawQueryParams.deals || 'true';
      }
    }

    const paramsKey = JSON.stringify(paramsForFetch);
    const filtersKey = availableFilters
      .map(f => f && f.key)
      .filter(Boolean)
      .sort()
      .join('|');
    const combinedKey = `${paramsKey}::${filtersKey}`;

    if (this.lastFilterCountsKey === combinedKey) {
      return;
    }

    this.lastFilterCountsKey = combinedKey;

    if (typeof this.props.onFetchFilterCounts === 'function') {
      const maybePromise = this.props.onFetchFilterCounts(paramsForFetch, availableFilters);
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.catch(() => {
          this.lastFilterCountsKey = null;
        });
      }
    }
  }

  handleResize() {
    if (typeof window !== 'undefined') {
      const newWidth = window.innerWidth;
      const currentWidth = this.state.windowWidth;

      // Only update if width changed significantly (more than 50px) to avoid too many updates
      if (Math.abs(newWidth - currentWidth) > 50) {
        this.setState({ windowWidth: newWidth }, () => {
          // Update perPage in URL after state update
          this.updatePerPageIfNeeded();
        });
      }
    }
    this.updateCtaVisibility();
  }

  updatePerPageIfNeeded() {
    const { windowWidth } = this.state;
    const {
      config,
      location,
      params: currentPathParams = {},
      perPageOverride,
      searchParams,
      onLoadData,
      setPerPageOverride: setOverride,
    } = this.props;

    if (typeof window === 'undefined') return;

    const rowsPerPage = windowWidth < 550 ? 12 : 4; // Show more rows on mobile
    const optimalPerPage = getOptimalPerPage(windowWidth, rowsPerPage);
    const currentPerPage = Number.isFinite(perPageOverride)
      ? perPageOverride
      : searchParams?.perPage != null
      ? parseInt(searchParams.perPage, 10)
      : null;

    // Only update if the optimal perPage is different from current
    if (currentPerPage !== optimalPerPage) {
      setOverride(optimalPerPage);

      // Re-fetch with the existing URL params (no perPage in URL)
      if (searchParams) {
        onLoadData(currentPathParams, location.search, config);
      }
    }
  }

  processFilterCountFetch() {
    const context = this.latestFilterFetchContext || {};
    const {
      listingsAreLoaded,
      searchParamsAreInSync,
      validQueryParams,
      availableFilters,
    } = context;

    if (listingsAreLoaded && searchParamsAreInSync) {
      this.maybeRequestFilterCounts(validQueryParams, availableFilters);
    } else if (!listingsAreLoaded) {
      this.lastFilterCountsKey = null;
    }
  }

  // Reset all filter query parameters
  handleResetAll(e) {
    this.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      intl,
      listings = [],
      location,
      history,
      onManageDisableScrolling,
      pagination,
      scrollingDisabled,
      searchInProgress,
      searchListingsError,
      searchParams = {},
      routeConfiguration,
      config,
      params: currentPathParams = {},
      currentUser,
    } = this.props;

    // If the search page variant is of type /s/:listingType, this defines the :listingType
    // path parameter used to filter the whole page.
    //
    // On a default search page (/s), this constant does not have a value, even when a
    // query parameter ?pub_listingType=[queryParamListingType] is used.
    const slugSearchParams = getSlugSearchParams(currentPathParams, config);
    const listingTypePathParam = slugSearchParams.pub_listingType;

    const { listingFields } = config?.listing || {};
    const { defaultFilters: defaultFiltersRaw, sortConfig } = config?.search || {};

    const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
    const defaultFiltersConfig = listingTypePathParam
      ? defaultFiltersRaw.filter(f => f.key !== 'listingType')
      : defaultFiltersRaw;

    const marketplaceCurrency = config.currency;
    const categoryConfiguration = config.categoryConfiguration;
    const listingCategories = categoryConfiguration.categories;
    const listingFieldsConfig = pickListingFieldFilters({
      listingFields,
      locationSearch: location.search,
      categoryConfiguration,
      activeListingTypes,
      currentPathParams,
    });
    const filterConfigsBase = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    // Page transition might initially use values from previous search
    // urlQueryParams doesn't contain page specific url params
    // like mapSearch, page or origin (origin depends on config.maps.search.sortSearchByDistance)
    const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
      location.search,
      searchParams,
      filterConfigsBase,
      sortConfig,
      isOriginInUse(config),
      currentPathParams,
      config
    );
    const validQueryParams = urlQueryParams;
    const slugFromPath = location?.pathname?.startsWith('/products/')
      ? location.pathname.replace('/products/', '').split('/')[0]
      : null;
    const breadcrumbParams =
      currentPathParams?.slug || slugFromPath
        ? {
            ...validQueryParams,
            ...getSlugSearchParams({ slug: currentPathParams?.slug || slugFromPath }, config),
          }
        : validQueryParams;
    // On a SEO slug page (/products/:slug) the filtering params (brand/category/
    // connectivity...) come from the slug entry, not the URL query string. Merge
    // them so the heading, active-filter state and filter counts are
    // contextualized like a normal filtered search (the data fetch already
    // applies them via SearchPage.duck loadData). URL query params win on
    // conflicts (a user-applied filter overrides the slug default).
    const slugForParams = currentPathParams?.slug || slugFromPath;
    const effectiveQueryParams = slugForParams
      ? { ...getSlugSearchParams({ slug: slugForParams }, config), ...validQueryParams }
      : validQueryParams;
    const isKeywordSearch = isMainSearchTypeKeywords(config);
    const hideCategoryFilter = !!currentPathParams?.slug;
    const builtInPrimaryFilters = defaultFiltersConfig
      .filter(f => ['categoryLevel', 'listingType'].includes(f.key))
      .filter(f => !(hideCategoryFilter && f.key === 'categoryLevel'));
    const builtInFilters = isKeywordSearch
      ? defaultFiltersConfig.filter(
          f => !['keywords', 'categoryLevel', 'listingType'].includes(f.key)
        )
      : defaultFiltersConfig.filter(f => !['categoryLevel', 'listingType'].includes(f.key));
    const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
      listingFieldsConfig,
      activeListingTypes
    );
    const availableFilters = [
      ...builtInPrimaryFilters,
      ...customPrimaryFilters,
      ...builtInFilters,
      ...customSecondaryFilters,
    ];

    const hasPaginationInfo = !!pagination && pagination.totalItems != null;
    const totalItems =
      searchParamsAreInSync && hasPaginationInfo
        ? pagination.totalItems
        : pagination?.paginationUnsupported
        ? listings.length
        : 0;
    const listingsAreLoaded =
      !searchInProgress &&
      searchParamsAreInSync &&
      !!(hasPaginationInfo || pagination?.paginationUnsupported);

    const filterOptionCounts = searchParamsAreInSync ? this.props.filterOptionCounts || {} : {};

    // Build dynamic enum options for brand filter from current page listings.
    // Only use them when URL params and results are in sync to avoid stale options.
    const shouldUseDynamicBrandOptions = listingsAreLoaded && searchParamsAreInSync;
    const dynamicBrandOptions = (() => {
      if (!shouldUseDynamicBrandOptions) {
        this.cachedListingsForBrands = null;
        this.cachedBrandOptions = null;
        return [];
      }
      if (this.cachedListingsForBrands === listings && this.cachedBrandOptions) {
        return this.cachedBrandOptions;
      }

      try {
        const brandValues = (listings || [])
          .map(l => l?.attributes?.publicData?.brand)
          .filter(v => typeof v === 'string' && v.trim().length > 0);
        const unique = Array.from(new Set(brandValues));
        const options = unique
          .sort((a, b) => a.localeCompare(b))
          .map(v => ({ option: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));

        // Cache the result
        this.cachedListingsForBrands = listings;
        this.cachedBrandOptions = options;
        return options;
      } catch (e) {
        return [];
      }
    })();

    // Cache listingFieldsConfigWithDynamicBrands to avoid recreating on every render
    const cacheKey = JSON.stringify([
      listingFieldsConfig.map(f => f.key),
      dynamicBrandOptions.map(o => o.option),
    ]);

    const listingFieldsConfigWithDynamicBrands = (() => {
      if (this.cachedListingFieldsConfigInput === cacheKey && this.cachedListingFieldsConfig) {
        return this.cachedListingFieldsConfig;
      }

      const result = listingFieldsConfig.map(f => {
        if (f.key === 'brand') {
          const existing = Array.isArray(f.enumOptions) ? f.enumOptions : [];
          // Merge existing configured options with dynamic ones from listings
          const mergedMap = new Map();
          existing.forEach(o => mergedMap.set(`${o.option}`, o));
          dynamicBrandOptions.forEach(o => {
            if (!mergedMap.has(`${o.option}`)) mergedMap.set(`${o.option}`, o);
          });
          const merged = Array.from(mergedMap.values());
          return { ...f, enumOptions: merged };
        }
        return f;
      });

      this.cachedListingFieldsConfigInput = cacheKey;
      this.cachedListingFieldsConfig = result;
      return result;
    })();

    const filterConfigs = {
      listingFieldsConfig: listingFieldsConfigWithDynamicBrands,
      defaultFiltersConfig,
      listingCategories,
      activeListingTypes,
      currentPathParams,
    };

    this.latestFilterFetchContext = {
      listingsAreLoaded,
      searchParamsAreInSync,
      validQueryParams: effectiveQueryParams,
      availableFilters,
    };

    // Selected aka active filters
    const selectedFilters = effectiveQueryParams;
    const isValidDatesFilter =
      searchParamsInURL.dates == null ||
      (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);
    const keysOfSelectedFilters = Object.keys(selectedFilters);
    // Exclude sort parameter and keywords from filter count
    const excludedParams = ['keywords', sortConfig.queryParamName];
    const selectedFiltersCountForMobile = isKeywordSearch
      ? keysOfSelectedFilters.filter(f => !excludedParams.includes(f)).length
      : keysOfSelectedFilters.filter(f => f !== sortConfig.queryParamName).length;

    const conflictingFilterActive = isAnyFilterActive(
      sortConfig.conflictingFilters,
      validQueryParams,
      filterConfigs
    );

    const showCreateListingsLink = false;
    const sortParamName = sortConfig?.queryParamName;
    const rawQueryParams = parse(location.search);
    const isPromotionsPage = isPromotionsPath(location.pathname);
    const isDealsActive =
      rawQueryParams.deals === 'true' ||
      rawQueryParams.deals === true ||
      rawQueryParams.deals === '1' ||
      rawQueryParams.deals === 1 ||
      isPromotionsPage;
    const hasExplicitSort = sortParamName
      ? Object.prototype.hasOwnProperty.call(rawQueryParams, sortParamName)
      : false;
    const nonFilterKeys = [sortParamName, 'perPage'].filter(Boolean);
    const hasFilterParams = Object.keys(searchParamsInURL || {}).some(
      key => !nonFilterKeys.includes(key)
    );
    const shouldRandomizeResults =
      !listingTypePathParam && !isDealsActive && !hasExplicitSort && !hasFilterParams;

    const sortValueForUi = shouldRandomizeResults
      ? null
      : validQueryParams[sortConfig.queryParamName];
    const sortBy = mode => {
      return sortConfig.active ? (
        <SortBy
          sort={sortValueForUi}
          isConflictingFilterActive={!!conflictingFilterActive}
          hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
          isRandomSortActive={shouldRandomizeResults}
          selectedFilters={selectedFilters}
          onSelect={this.handleSortBy}
          showAsPopup
          mode={mode}
          contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
        />
      ) : null;
    };
    const noResultsInfo = (
      <NoSearchResultsMaybe
        listingsAreLoaded={listingsAreLoaded}
        totalItems={totalItems}
        location={location}
        resetAll={this.resetAll}
        showCreateListingsLink={showCreateListingsLink}
      />
    );

    // Get heading based on active filters (slug params included so SEO slug
    // pages like /products/quandify are contextualized, not generic).
    const brandName = getActiveBrandName(
      effectiveQueryParams,
      listingFieldsConfigWithDynamicBrands
    );
    const connectivityName = getActiveConnectivityName(
      effectiveQueryParams,
      listingFieldsConfigWithDynamicBrands
    );
    const compatibilityName = getActiveCompatibilityName(
      effectiveQueryParams,
      listingFieldsConfigWithDynamicBrands
    );
    const categoryName = getActiveCategoryName(effectiveQueryParams, listingCategories);

    // A SEO slug page (/products/:slug) is a filtered view even when the URL has
    // no query string, so it must not be treated as "no filters" (otherwise it
    // shows the generic "1000+ products" message and hides the real count).
    const noFiltersApplied =
      !hasFilterParams && !isDealsActive && !listingTypePathParam && !slugForParams;
    const slugParam = currentPathParams?.slug ?? slugFromPath;
    const slugEntry = findSlugEntry(slugParam ?? '');
    const isRootPath = !slugParam;
    const shouldUseRootSlugEntry = isRootPath && noFiltersApplied;
    const effectiveSlugEntry = isRootPath ? (shouldUseRootSlugEntry ? slugEntry : null) : slugEntry;
    const resultsMessage =
      noFiltersApplied || shouldUseRootSlugEntry ? (
        <FormattedMessage id="SearchPage.moreThanThousandProducts" />
      ) : null;
    const resultsCountForUi = shouldUseRootSlugEntry ? null : totalItems;

    // Count how many specific filters are active (brand, connectivity, compatibility)
    const activeSpecificFiltersCount = [brandName, connectivityName, compatibilityName].filter(
      Boolean
    ).length;

    // Determine the heading based on combination of filters and deals
    let categoryHeading;

    if (isDealsActive) {
      // Deals mode is active
      if (categoryName && brandName) {
        // Category + brand + deals: "comme aujourd'hui + en promotion"
        categoryHeading = `${categoryName} ${brandName} en promotion`;
      } else if (categoryName && connectivityName) {
        // Category + connectivity + deals
        categoryHeading = `${categoryName} ${connectivityName} en promotion`;
      } else if (categoryName && compatibilityName) {
        // Category + compatibility + deals
        categoryHeading = `${categoryName} compatibles ${compatibilityName} en promotion`;
      } else if (categoryName) {
        // Category + deals
        categoryHeading = `${categoryName} en promotion`;
      } else if (brandName) {
        // Brand + deals: "Nos bons plans Marque"
        categoryHeading = `Nos bons plans ${brandName}`;
      } else if (connectivityName) {
        // Connectivity + deals: "Nos bons plans connectivité"
        categoryHeading = `Nos bons plans ${connectivityName}`;
      } else if (compatibilityName) {
        // Compatibility + deals
        categoryHeading = `Nos bons plans compatibles ${compatibilityName}`;
      } else {
        // Deals only: "Nos bons plans & Promotions"
        categoryHeading = 'Nos bons plans & Promotions';
      }
    } else {
      // Normal mode (no deals)
      if (categoryName) {
        // Category is active
        if (activeSpecificFiltersCount === 0) {
          // Only category
          categoryHeading = categoryName;
        } else if (activeSpecificFiltersCount === 1) {
          // Category + one specific filter
          if (brandName) {
            categoryHeading = `${categoryName} ${brandName}`;
          } else if (connectivityName) {
            categoryHeading = `${categoryName} ${connectivityName}`;
          } else if (compatibilityName) {
            categoryHeading = `${categoryName} compatibles ${compatibilityName}`;
          }
        } else {
          // Category + multiple specific filters
          categoryHeading = categoryName;
        }
      } else {
        // No category filter
        if (activeSpecificFiltersCount === 1) {
          // Only one specific filter
          if (brandName) {
            categoryHeading = `Nos produits ${brandName}`;
          } else if (connectivityName) {
            categoryHeading = `Nos produits ${connectivityName}`;
          } else if (compatibilityName) {
            categoryHeading = `Nos produits compatibles ${compatibilityName}`;
          }
        } else {
          // Multiple specific filters or no filters at all
          categoryHeading = intl.formatMessage({ id: 'SearchPage.defaultCatalogueHeading' });
        }
      }
    }

    const categoryIdForSEO =
      effectiveSlugEntry?.categoryId ||
      searchParamsInURL.pub_categoryLevel1 ||
      searchParamsInURL.category;
    const subcategoryIdForSEO =
      effectiveSlugEntry?.subcategoryId ||
      searchParamsInURL.pub_categoryLevel2 ||
      searchParamsInURL.subcategory;
    const categorySeoData =
      effectiveSlugEntry?.seo ||
      (categoryIdForSEO ? getSeoByCategoryIds(categoryIdForSEO, subcategoryIdForSEO) : null);
    const categoryDescription =
      effectiveSlugEntry && categorySeoData?.metaDescription
        ? categorySeoData.metaDescription
        : null;
    const categoryIntro =
      effectiveSlugEntry && categorySeoData?.intro ? categorySeoData.intro : null;
    const faqItems = Array.isArray(categorySeoData?.faq) ? categorySeoData.faq : [];
    if (effectiveSlugEntry && categorySeoData?.h1) {
      categoryHeading = categorySeoData.h1;
    }
    const slugPageLoading = currentPathParams?.slug && !this.state.slugDataReady;
    const headingToRender = slugPageLoading ? null : categoryHeading;
    const introToRender = slugPageLoading ? null : categoryIntro;

    const relatedLinks = effectiveSlugEntry ? getRelatedLinks(effectiveSlugEntry) : [];
    const guideLink = effectiveSlugEntry?.guideLink || null;
    const comparatorCta = effectiveSlugEntry?.comparatorCta || null;
    const mergedCtaItems = relatedLinks;
    const showCtaBanner = mergedCtaItems.length > 0;
    const mobileGuideComparatorCtas =
      guideLink?.url || comparatorCta?.url ? (
        <div className={css.mobileCtaRow}>
          <div className={css.seoCategoryHeadingCtasPair}>
            {guideLink?.url ? (
              <a
                className={`${css.categoryGuideButton} ${css.categoryCtaFixedButton}`}
                href={guideLink.url}
                onClick={e => {
                  if (guideLink.url.startsWith('/')) {
                    e.preventDefault();
                    history.push(guideLink.url);
                  }
                }}
              >
                <span className={css.categoryCtaIcon} aria-hidden="true">
                  <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none">
                    <g fill="currentColor">
                      <path d="M12.75 1a.75.75 0 000 1.5h.69l-1.97 1.97a.75.75 0 001.06 1.06l1.97-1.97v.69a.75.75 0 001.5 0v-2.5a.75.75 0 00-.75-.75h-2.5z"></path>
                      <path
                        fillRule="evenodd"
                        d="M1.25 2C.56 2 0 2.56 0 3.25v8.5C0 12.44.56 13 1.25 13H5c.896 0 1.475.205 1.809.448.317.23.441.51.441.802a.751.751 0 101.5 0c0-.292.124-.572.441-.802.334-.243.913-.448 1.809-.448h3.75c.69 0 1.25-.56 1.25-1.25v-4.5a.75.75 0 00-1.5 0v4.25H11c-.878 0-1.64.158-2.25.467v-6.55c0-.788.376-1.42 1.12-1.722a.75.75 0 00-.561-1.39 3.27 3.27 0 00-1.31.941A3.13 3.13 0 007.773 3C7.106 2.354 6.154 2 5 2H1.25zm6 3.417c0-.553-.187-1.015-.522-1.34C6.394 3.753 5.846 3.5 5 3.5H1.5v8H5c.878 0 1.64.158 2.25.467v-6.55z"
                        clipRule="evenodd"
                      ></path>
                    </g>
                  </svg>
                </span>
                {guideLink.label || 'Voir le guide'}
              </a>
            ) : null}
            {comparatorCta?.url ? (
              <a
                className={`${css.categoryGuideButton} ${css.categoryCtaFixedButton}`}
                href={comparatorCta.url}
                onClick={e => {
                  if (comparatorCta.url.startsWith('/')) {
                    e.preventDefault();
                    history.push(comparatorCta.url);
                  }
                }}
              >
                <span className={css.categoryCtaIcon} aria-hidden="true">
                  <svg fill="currentColor" viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                    <g>
                      <path d="M10.08,7l1,1,3.44-3.45L11,1,10,2l1.8,1.8H2v1.4h9.82ZM5.86,9l-1-1L1.42,11.5,4.91,15l1-1L4.1,12.2H14V10.8H4.1Z"></path>
                    </g>
                  </svg>
                </span>
                {comparatorCta.label || 'Comparer'}
              </a>
            ) : null}
          </div>
        </div>
      ) : null;
    const mobileRelatedLinksCta = showCtaBanner ? (
      <div className={css.categoryCtaBanner}>
        <div className={css.categoryCtaContent}>
          <span className={css.categoryCtaLabel}>Voir aussi :</span>
          <div className={css.categoryCtaInline}>
            {mergedCtaItems.map(item => (
              <NamedLink
                key={item.slug}
                name="SearchPageWithSlug"
                params={{ slug: item.slug }}
                className={`${css.categoryCtaButton} ${css.categoryCtaInlineButton}`}
              >
                {item.label || item.slug}
              </NamedLink>
            ))}
          </div>
        </div>
      </div>
    ) : null;

    const { title, description, schema } = createSearchResultSchema(
      listings,
      {
        ...(searchParamsInURL || {}),
        ...(isDealsActive ? { deals: 'true' } : {}),
      },
      intl,
      routeConfiguration,
      config
    );
    const resolvedTitle = categorySeoData?.title || title;
    const resolvedDescription = categorySeoData?.metaDescription || description;
    const faqSchema = faqItems.length
      ? {
          '@type': 'FAQPage',
          mainEntity: faqItems.map(item => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: item.answer,
            },
          })),
        }
      : null;
    const baseSchema = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
    const pageSchema = faqSchema ? [...baseSchema, faqSchema] : schema;

    // Set topbar class based on if a modal is open in
    // a child component
    const topbarClasses = this.state.isMobileModalOpen
      ? classNames(css.topbarBehindModal, css.topbar)
      : css.topbar;

    // N.B. openMobileMap button is sticky.
    // For some reason, stickyness doesn't work on Safari, if the element is <button>
    return (
      <Page
        scrollingDisabled={scrollingDisabled}
        description={resolvedDescription}
        title={resolvedTitle}
        schema={pageSchema}
      >
        <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
        <div className={css.layoutWrapperContainer}>
          <aside
            className={classNames(css.layoutWrapperFilterColumn, {
              [css.layoutWrapperFilterColumnCollapsed]: this.state.isAsideCollapsed,
            })}
            data-testid="filterColumnAside"
          >
            <div className={css.filterColumnContent}>
              {!this.state.isAsideCollapsed && (
                <>
                  <h2 className={css.filtersHeading}>
                    <FormattedMessage id="SearchPage.filtersHeading" />
                  </h2>
                  {availableFilters.map(filterConfig => {
                    const key = `SearchFiltersDesktop.${filterConfig.scope || 'built-in'}.${
                      filterConfig.key
                    }`;
                    // Inject dynamic brand options when rendering filters
                    const cfg =
                      filterConfig.key === 'brand'
                        ? listingFieldsConfigWithDynamicBrands.find(f => f.key === 'brand') ||
                          filterConfig
                        : filterConfig;
                    return (
                      <FilterComponent
                        key={key}
                        idPrefix="SearchFiltersDesktop"
                        className={css.filter}
                        config={cfg}
                        listingCategories={listingCategories}
                        marketplaceCurrency={marketplaceCurrency}
                        urlQueryParams={effectiveQueryParams}
                        filterOptionCounts={filterOptionCounts}
                        initialValues={initialValues(this.props, {
                          ...effectiveQueryParams,
                          ...this.state.currentQueryParams,
                        })}
                        getHandleChangedValueFn={this.getHandleChangedValueFn}
                        intl={intl}
                        showCounts={false}
                        liveEdit
                        showAsPopup={false}
                        isDesktop
                      />
                    );
                  })}
                  <button className={css.resetAllButton} onClick={e => this.handleResetAll(e)}>
                    <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
                  </button>
                </>
              )}
            </div>
            <button
              type="button"
              className={classNames(css.toggleAsideButton, {
                [css.toggleAsideButtonCollapsed]: this.state.isAsideCollapsed,
              })}
              onClick={this.toggleAside}
              aria-label={
                this.state.isAsideCollapsed
                  ? intl.formatMessage({ id: 'SearchPage.expandFilters' })
                  : intl.formatMessage({ id: 'SearchPage.collapseFilters' })
              }
              aria-expanded={!this.state.isAsideCollapsed}
            >
              <IconArrowHead
                direction={this.state.isAsideCollapsed ? 'right' : 'left'}
                size="small"
              />
            </button>
          </aside>

          <div
            className={classNames(css.layoutWrapperMain, {
              [css.layoutWrapperMainExpanded]: this.state.isAsideCollapsed,
            })}
            role="main"
          >
            <div className={css.searchResultContainer}>
              {!isRootPath &&
              (hasFilterParams ||
                isDealsActive ||
                listingTypePathParam ||
                currentPathParams?.slug ||
                slugFromPath) ? (
                <SearchBreadcrumb queryParams={breadcrumbParams} onResetAll={this.resetAll} />
              ) : null}
              {/* Mobile category heading */}
              <div className={css.mobileHeadingWrapper}>
                {slugPageLoading ? (
                  <div className={css.categoryHeadingSpinner}>
                    <IconSpinner />
                  </div>
                ) : (
                  <>
                    <h1 className={css.mobileHeading}>{headingToRender}</h1>
                    {introToRender ? (
                      <p className={css.mobileCategoryIntro}>{introToRender}</p>
                    ) : null}
                  </>
                )}
                {mobileGuideComparatorCtas || mobileRelatedLinksCta ? (
                  <div className={css.mobileCtaWrapper}>
                    {mobileGuideComparatorCtas}
                    {mobileRelatedLinksCta}
                  </div>
                ) : null}
              </div>
              <SearchFiltersMobile
                className={css.searchFiltersMobileList}
                urlQueryParams={effectiveQueryParams}
                sortByComponent={sortBy('mobile')}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={resultsCountForUi}
                resultsMessage={resultsMessage}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                showAsModalMaxWidth={MODAL_BREAKPOINT}
                onManageDisableScrolling={onManageDisableScrolling}
                onOpenModal={this.onOpenMobileModal}
                onCloseModal={this.onCloseMobileModal}
                resetAll={this.resetAll}
                selectedFiltersCount={selectedFiltersCountForMobile}
                isMapVariant={false}
                noResultsInfo={noResultsInfo}
                location={location}
              >
                {availableFilters.map(filterConfig => {
                  const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  const cfg =
                    filterConfig.key === 'brand'
                      ? listingFieldsConfigWithDynamicBrands.find(f => f.key === 'brand') ||
                        filterConfig
                      : filterConfig;
                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersMobile"
                      config={cfg}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={effectiveQueryParams}
                      filterOptionCounts={filterOptionCounts}
                      initialValues={initialValues(this.props, {
                        ...effectiveQueryParams,
                        ...this.state.currentQueryParams,
                      })}
                      getHandleChangedValueFn={this.getHandleChangedValueFn}
                      intl={intl}
                      showCounts={false}
                      liveEdit
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersMobile>
              {slugPageLoading ? (
                <div className={css.categoryHeadingSpinner}>
                  <IconSpinner />
                </div>
              ) : null}
              <MainPanelHeader
                className={css.mainPanel}
                sortByComponent={sortBy('desktop')}
                isSortByActive={sortConfig.active}
                listingsAreLoaded={listingsAreLoaded}
                resultsCount={resultsCountForUi}
                resultsMessage={resultsMessage}
                searchInProgress={searchInProgress}
                searchListingsError={searchListingsError}
                noResultsInfo={noResultsInfo}
                categoryHeading={headingToRender}
                categoryIntro={introToRender}
                headingCtas={
                  guideLink?.url || comparatorCta?.url ? (
                    <div className={css.seoCategoryHeadingCtasPair}>
                      {guideLink?.url ? (
                        <a
                          className={`${css.categoryGuideButton} ${css.categoryCtaFixedButton}`}
                          href={guideLink.url}
                          onClick={e => {
                            if (guideLink.url.startsWith('/')) {
                              e.preventDefault();
                              history.push(guideLink.url);
                            }
                          }}
                        >
                          <span className={css.categoryCtaIcon} aria-hidden="true">
                            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" fill="none">
                              <g fill="currentColor">
                                <path d="M12.75 1a.75.75 0 000 1.5h.69l-1.97 1.97a.75.75 0 001.06 1.06l1.97-1.97v.69a.75.75 0 001.5 0v-2.5a.75.75 0 00-.75-.75h-2.5z"></path>
                                <path
                                  fillRule="evenodd"
                                  d="M1.25 2C.56 2 0 2.56 0 3.25v8.5C0 12.44.56 13 1.25 13H5c.896 0 1.475.205 1.809.448.317.23.441.51.441.802a.751.751 0 101.5 0c0-.292.124-.572.441-.802.334-.243.913-.448 1.809-.448h3.75c.69 0 1.25-.56 1.25-1.25v-4.5a.75.75 0 00-1.5 0v4.25H11c-.878 0-1.64.158-2.25.467v-6.55c0-.788.376-1.42 1.12-1.722a.75.75 0 00-.561-1.39 3.27 3.27 0 00-1.31.941A3.13 3.13 0 007.773 3C7.106 2.354 6.154 2 5 2H1.25zm6 3.417c0-.553-.187-1.015-.522-1.34C6.394 3.753 5.846 3.5 5 3.5H1.5v8H5c.878 0 1.64.158 2.25.467v-6.55z"
                                  clipRule="evenodd"
                                ></path>
                              </g>
                            </svg>
                          </span>
                          {guideLink.label || 'Voir le guide'}
                        </a>
                      ) : null}
                      {comparatorCta?.url ? (
                        <a
                          className={`${css.categoryGuideButton} ${css.categoryCtaFixedButton}`}
                          href={comparatorCta.url}
                          onClick={e => {
                            if (comparatorCta.url.startsWith('/')) {
                              e.preventDefault();
                              history.push(comparatorCta.url);
                            }
                          }}
                        >
                          <span className={css.categoryCtaIcon} aria-hidden="true">
                            <svg
                              fill="currentColor"
                              viewBox="0 0 16 16"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g>
                                <path d="M10.08,7l1,1,3.44-3.45L11,1,10,2l1.8,1.8H2v1.4h9.82ZM5.86,9l-1-1L1.42,11.5,4.91,15l1-1L4.1,12.2H14V10.8H4.1Z"></path>
                              </g>
                            </svg>
                          </span>
                          {comparatorCta.label || 'Comparer'}
                        </a>
                      ) : null}
                    </div>
                  ) : null
                }
                ctaContent={
                  showCtaBanner ? (
                    <div className={css.categoryCtaBanner} ref={this.setCtaBannerRef}>
                      <div className={css.categoryCtaContent}>
                        <span className={css.categoryCtaLabel}>Voir aussi :</span>
                        <div className={css.categoryCtaInline}>
                          {mergedCtaItems.map(item => (
                            <NamedLink
                              key={item.slug}
                              name="SearchPageWithSlug"
                              params={{ slug: item.slug }}
                              className={`${css.categoryCtaButton} ${css.categoryCtaInlineButton}`}
                            >
                              {item.label || item.slug}
                            </NamedLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null
                }
              />
              <div className={css.listingsForGridVariant}>
                {searchListingsError ? (
                  <H3 className={css.error}>
                    <FormattedMessage id="SearchPage.searchError" />
                  </H3>
                ) : null}
                {!isValidDatesFilter ? (
                  <H5>
                    <FormattedMessage id="SearchPage.invalidDatesFilter" />
                  </H5>
                ) : null}
                {!(listingsAreLoaded || searchListingsError) ? (
                  <div className={css.listingsLoading} role="status" aria-live="polite">
                    <IconSpinner />
                    <p className={css.listingsLoadingText}>
                      <FormattedMessage id="MainPanelHeader.loadingResults" />
                    </p>
                  </div>
                ) : (
                  <>
                    <SearchResultsPanel
                      className={css.searchListingsPanel}
                      listings={listings}
                      pagination={pagination}
                      search={searchParamsInURL}
                      isMapVariant={false}
                      slugParam={currentPathParams?.slug}
                      isPromotionsPage={isPromotionsPage}
                    />
                    {faqItems.length > 0 ? (
                      <section className={css.faqSection}>
                        <h2 className={css.faqHeading}>FAQ</h2>
                        <div className={css.faqList}>
                          {faqItems.map((item, index) => (
                            <div key={`${item.question}-${index}`} className={css.faqItem}>
                              <h3 className={css.faqQuestion}>{item.question}</h3>
                              <p className={css.faqAnswer}>{item.answer}</p>
                            </div>
                          ))}
                        </div>
                      </section>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <FooterContainer />
      </Page>
    );
  }
}

/**
 * SearchPage component with grid layout (no map)
 *
 * @param {Object} props
 * @param {propTypes.currentUser} [props.currentUser] - The current user
 * @param {Array<propTypes.listing>} [props.listings] - The listings
 * @param {propTypes.pagination} [props.pagination] - The pagination
 * @param {boolean} [props.scrollingDisabled] - Whether the scrolling is disabled
 * @param {boolean} [props.searchInProgress] - Whether the search is in progress
 * @param {propTypes.error} [props.searchListingsError] - The search listings error
 * @param {Object} [props.searchParams] - The search params from the Redux state
 * @param {Function} [props.onManageDisableScrolling] - The function to manage the disable scrolling
 * @returns {JSX.Element}
 */
const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    filterOptionCounts,
    perPageOverride,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    filterOptionCounts,
    perPageOverride,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchFilterCounts: (searchParams, filters) =>
    dispatch(fetchFilterOptionCounts(searchParams, filters)),
  onLoadData: (params, search, config) => dispatch(loadData(params, search, config)),
  setPerPageOverride: perPage => dispatch(setPerPageOverride(perPage)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
